import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import './GeneratedJDs.css';
import {
  Search,
  Plus,
  FileText,
  Eye,
  Download,
  Pencil,
  Trash2,
  Calendar,
  Loader2,
  X,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  SlidersHorizontal,
  FileUp,
} from 'lucide-react';

const ITEMS_PER_PAGE = 10;

const GeneratedJDs = () => {
  const [jds, setJds] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [currentUserRole, setCurrentUserRole] = useState(null);

  // Search, Sort, Pagination
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);

  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  /* ── Fetch ───────────────────────────────────────────────── */
  useEffect(() => {
    const recId = localStorage.getItem('recruiterId');
    const custId = localStorage.getItem('customerId');

    if (recId) {
      setCurrentUserId(recId);
      setCurrentUserRole('Recruiter');
      fetchJDs(recId);
    } else if (custId) {
      setCurrentUserId(custId);
      setCurrentUserRole('Customer');
      fetchJDs(custId);
    } else {
      setIsLoading(false);
    }
  }, []);

  const fetchJDs = async (activeId) => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/job-description`
      );
      const myJds = response.data.filter((jd) => jd.creatorId === activeId);
      setJds(myJds);
    } catch (error) {
      console.error('Failed to fetch JDs:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Reset page on filter change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, sortField, sortDir]);

  /* ── Handlers ────────────────────────────────────────────── */
  const handleEdit = (id) => {
    navigate(`/recruitment-dashboard?tab=generator&id=${id}`);
  };

  const handleDownload = (pdfPath, jdName) => {
    if (!pdfPath) return alert('No PDF file found for this JD.');
    const link = document.createElement('a');
    link.href = pdfPath;
    link.target = '_blank';
    link.download = `${jdName || 'JD'}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to permanently delete this JD?')) {
      try {
        await axios.delete(
          `${process.env.REACT_APP_API_BASE_URL}/api/job-description/${id}`
        );
        setJds(jds.filter((jd) => jd._id !== id));
      } catch (error) {
        console.error('Failed to delete JD:', error);
        alert('Could not delete the Job Description.');
      }
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const jdName = window.prompt('Please enter a name for this Job Description:');
    if (!jdName || jdName.trim() === '') {
      alert('Upload cancelled. A name is required.');
      event.target.value = null;
      return;
    }

    const formData = new FormData();
    formData.append('jdFile', file);
    formData.append('jd_name', jdName.trim());

    if (currentUserId && currentUserRole) {
      formData.append('creatorId', currentUserId);
      formData.append('creatorRole', currentUserRole);
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/job-description/upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      const newJd = response.data.data || response.data;
      setJds((prev) => [newJd, ...prev]);
      alert('JD uploaded successfully!');
    } catch (error) {
      console.error('Failed to upload JD:', error);
      alert('Could not upload the JD.');
    } finally {
      event.target.value = null;
    }
  };

  const handleAddJdClick = () => fileInputRef.current.click();

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir(field === 'date' ? 'desc' : 'asc');
    }
  };

  /* ── Processed List ──────────────────────────────────────── */
  const processed = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();

    let list = jds.filter((jd) => {
      if (!q) return true;
      return (
        (jd.jd_name || '').toLowerCase().includes(q) ||
        (jd.job_role || '').toLowerCase().includes(q)
      );
    });

    if (sortField) {
      list = [...list].sort((a, b) => {
        if (sortField === 'date') {
          const aV = new Date(a.createdAt).getTime() || 0;
          const bV = new Date(b.createdAt).getTime() || 0;
          return sortDir === 'asc' ? aV - bV : bV - aV;
        }
        if (sortField === 'name') {
          const aV = (a.jd_name || '').toLowerCase();
          const bV = (b.jd_name || '').toLowerCase();
          return sortDir === 'asc' ? aV.localeCompare(bV) : bV.localeCompare(aV);
        }
        return 0;
      });
    }

    return list;
  }, [jds, searchQuery, sortField, sortDir]);

  /* ── Pagination ──────────────────────────────────────────── */
  const totalPages = Math.ceil(processed.length / ITEMS_PER_PAGE);
  const paginated = processed.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const getPageNums = () => {
    const pages = [];
    const max = 5;
    let start = Math.max(1, currentPage - Math.floor(max / 2));
    let end = Math.min(totalPages, start + max - 1);
    if (end - start < max - 1) start = Math.max(1, end - max + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  const SortIcon = ({ field }) => {
    const active = sortField === field;
    return (
      <span className="gj-sort-icons">
        <ChevronUp size={11} className={active && sortDir === 'asc' ? 'gj-si-on' : ''} />
        <ChevronDown size={11} className={active && sortDir === 'desc' ? 'gj-si-on' : ''} />
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return 'Invalid Date';
    }
  };

  /* ── Loading ─────────────────────────────────────────────── */
  if (isLoading) {
    return (
      <div className="gj-root">
        <div className="gj-bg-blob gj-blob-1" />
        <div className="gj-bg-blob gj-blob-2" />
        <div className="gj-loading">
          <div className="gj-loading-card">
            <Loader2 className="gj-spin" size={36} />
            <span>Loading saved JDs…</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="gj-root">
      {/* Background blobs */}
      <div className="gj-bg-blob gj-blob-1" />
      <div className="gj-bg-blob gj-blob-2" />

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileSelect}
        accept=".pdf"
      />

      {/* ═══ Header ═══ */}
      <div className="gj-header">
        <div className="gj-header-left">
          <div className="gj-title-icon">
            <FileText size={28} />
          </div>
          <div>
            <h1 className="gj-title">Saved Job Descriptions</h1>
            <p className="gj-subtitle">
              Manage and edit all your generated and uploaded JDs
            </p>
          </div>
        </div>

        <div className="gj-header-right">
          <span className="gj-count-badge">
            <FileText size={14} />
            {processed.length} JD{processed.length !== 1 ? 's' : ''}
          </span>
          <button className="gj-add-btn" onClick={handleAddJdClick}>
            <Plus size={16} />
            Add JD
          </button>
        </div>
      </div>

      {/* ═══ Controls ═══ */}
      <div className="gj-controls">
        {/* Search */}
        <div className="gj-search-wrap">
          <Search size={16} className="gj-search-icon" />
          <input
            className="gj-search"
            type="text"
            placeholder="Search by JD name or job role…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="gj-clear-btn" onClick={() => setSearchQuery('')}>
              <X size={12} />
            </button>
          )}
        </div>

        {/* Sort */}
        <div className="gj-sort-bar">
          <span className="gj-sort-label">
            <SlidersHorizontal size={13} /> Sort
          </span>
          <button
            className={`gj-sort-btn ${sortField === 'date' ? 'active' : ''}`}
            onClick={() => handleSort('date')}
          >
            Date <SortIcon field="date" />
          </button>
          <button
            className={`gj-sort-btn ${sortField === 'name' ? 'active' : ''}`}
            onClick={() => handleSort('name')}
          >
            Name <SortIcon field="name" />
          </button>
        </div>
      </div>

      {/* Showing */}
      <p className="gj-showing">
        {searchQuery
          ? `Showing ${paginated.length} of ${processed.length} results for "${searchQuery}"`
          : `Showing ${paginated.length} of ${processed.length} JDs`}
      </p>

      {/* ═══ Cards Grid ═══ */}
      {paginated.length === 0 ? (
        <div className="gj-empty-card">
          <FileText size={44} className="gj-empty-icon" />
          <h3>{searchQuery ? `No JDs match "${searchQuery}"` : 'No Job Descriptions yet'}</h3>
          <p>
            {searchQuery
              ? 'Try a different search term'
              : 'Click "Add JD" to upload one or use the JD Generator to create your first description'}
          </p>
          {!searchQuery && (
            <button className="gj-add-btn-large" onClick={handleAddJdClick}>
              <FileUp size={16} /> Upload Your First JD
            </button>
          )}
        </div>
      ) : (
        <div className="gj-grid">
          {paginated.map((jd) => (
            <div key={jd._id} className="gj-card">
              <div className="gj-card-top">
                <div className="gj-card-icon">
                  <FileText size={22} />
                </div>
                <div className="gj-card-head">
                  <h3 className="gj-card-name" title={jd.jd_name}>
                    {jd.jd_name}
                  </h3>
                  {jd.job_role && jd.job_role !== jd.jd_name && (
                    <span className="gj-card-role">{jd.job_role}</span>
                  )}
                </div>
              </div>

              <div className="gj-card-meta">
                <Calendar size={12} /> Created {formatDate(jd.createdAt)}
              </div>

              <div className="gj-card-actions">
                <button
                  className="gj-action gj-action-view"
                  onClick={() => window.open(jd.pdf_path, '_blank')}
                  disabled={!jd.pdf_path}
                  title="Open PDF"
                >
                  <Eye size={14} /> View
                </button>
                <button
                  className="gj-action gj-action-download"
                  onClick={() => handleDownload(jd.pdf_path, jd.jd_name)}
                  disabled={!jd.pdf_path}
                  title="Download PDF"
                >
                  <Download size={14} />
                </button>
                <button
                  className="gj-action gj-action-edit"
                  onClick={() => handleEdit(jd._id)}
                  title="Edit in Generator"
                >
                  <Pencil size={14} />
                </button>
                <button
                  className="gj-action gj-action-delete"
                  onClick={() => handleDelete(jd._id)}
                  title="Delete"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ═══ Pagination ═══ */}
      {totalPages > 1 && (
        <div className="gj-pagination">
          <span className="gj-page-info">
            Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
          </span>
          <div className="gj-page-controls">
            <button
              className="gj-page-btn nav"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft size={14} />
            </button>
            {getPageNums().map((p) => (
              <button
                key={p}
                className={`gj-page-btn ${currentPage === p ? 'active' : ''}`}
                onClick={() => setCurrentPage(p)}
              >
                {p}
              </button>
            ))}
            <button
              className="gj-page-btn nav"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GeneratedJDs;